// Fotos ampliadas (<product-lightbox>): a lupa, ou um toque na foto da
// galeria, abre um <dialog> de tela inteira com todas as fotos num trilho
// com snap, setas no desktop e dedo no celular. Um toque na foto ampliada
// aproxima 2x no ponto tocado, e o arraste passeia por ela. Ao fechar, a
// galeria da página fica na foto em que se parou.
//
// No molde do zoom do Minimog (PhotoSwipe), sem biblioteca: é o mesmo
// trilho nativo do tema, com o carousel.js ligando as setas.

class ProductLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.rail = this.querySelector('[data-rail]');
    this.page = this.closest('product-page');
    if (!this.dialog || !this.rail) return;

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    // A lupa e a foto vivem na galeria, fora daqui.
    this.page?.addEventListener('click', this.handleGalleryClick, { signal });
    this.dialog.addEventListener('click', this.handleDialogClick, { signal });
    this.dialog.addEventListener('cancel', this.handleCancel, { signal });
    this.dialog.addEventListener('keydown', this.handleKeydown, { signal });
    this.rail.addEventListener('scroll', this.syncCounter, { passive: true, signal });
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  handleGalleryClick = (event) => {
    const trigger = event.target.closest('[data-zoom], .product__media-image');
    if (!trigger || this.dialog.contains(trigger)) return;
    const item = trigger.closest('[data-media-id]');
    if (!item) return;
    event.preventDefault();
    this.open(item.dataset.mediaId);
  };

  handleDialogClick = (event) => {
    if (event.target.closest('[data-close]')) return this.close();
    const image = event.target.closest('.product__lightbox-image');
    if (image) return this.toggleZoom(image.closest('.product__lightbox-item'), event);
    // Fora da foto e dos botões é o fundo: fecha, como todo lightbox.
    if (!event.target.closest('button, [data-counter]')) this.close();
  };

  // Esc fecha com a mesma saída animada do botão.
  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  handleKeydown = (event) => {
    if (event.key === 'ArrowRight') this.querySelector('[data-next]')?.click();
    if (event.key === 'ArrowLeft') this.querySelector('[data-prev]')?.click();
  };

  open(mediaId) {
    if (this.dialog.open) return;
    const target = this.rail.querySelector(`[data-lightbox-media="${CSS.escape(String(mediaId))}"]`);
    this.dialog.showModal();
    if (target) this.rail.scrollTo({ left: target.offsetLeft - this.rail.offsetLeft, behavior: 'instant' });
    this.syncCounter();
  }

  close() {
    if (!this.dialog.open || this.dialog.hasAttribute('closing')) return;
    this.dialog.setAttribute('closing', '');
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      this.dialog.removeAttribute('closing');
      this.dialog.close();
      this.leaveZoom();
      this.syncGallery();
    };
    this.dialog.addEventListener('transitionend', done, { once: true });
    // Sem transição (movimento reduzido) o evento não vem.
    setTimeout(done, 400);
  }

  // Aproxima no ponto tocado: a foto dobra e o item vira rolável, então
  // levar o scroll até a fração clicada deixa aquele ponto sob o dedo.
  toggleZoom(item, event) {
    if (!item) return;
    const zoomed = item.toggleAttribute('zoomed');
    this.rail.toggleAttribute('zoomed', zoomed);
    if (!zoomed) {
      item.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      return;
    }
    const rect = item.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    requestAnimationFrame(() => {
      item.scrollTo({
        left: (item.scrollWidth - item.clientWidth) * x,
        top: (item.scrollHeight - item.clientHeight) * y,
        behavior: 'instant',
      });
    });
  }

  leaveZoom() {
    this.rail.querySelectorAll('[zoomed]').forEach((el) => el.removeAttribute('zoomed'));
    this.rail.removeAttribute('zoomed');
  }

  // Cada item tem a largura do trilho, então a posição dividida pela
  // largura é o índice da foto à vista.
  syncCounter = () => {
    const items = Array.from(this.rail.children);
    if (!items.length) return;
    const index = Math.min(items.length - 1, Math.max(0, Math.round(this.rail.scrollLeft / this.rail.clientWidth)));
    this.current = items[index];
    const counter = this.querySelector('[data-current]');
    if (counter) counter.textContent = String(index + 1);
  };

  syncGallery() {
    const id = this.current?.dataset.lightboxMedia;
    if (id) this.page?.showMedia?.(id);
  }
}

if (!customElements.get('product-lightbox')) {
  customElements.define('product-lightbox', ProductLightbox);
}
